import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

import { useLatest } from '#/hooks/useLatest';
import {
  useSearchActions,
  useSearchLocation,
} from '#/modules/search/contexts/SearchContext';
import { searchTrackDisplayedListings } from '#/modules/search/helpers/searchTrackDisplayedListings';
import { useSearchJobsRouteLoaderData } from '#/modules/search/hooks/useSearchJobsRouteLoaderData';
import { useSearchReady } from '#/modules/search/hooks/useSearchReady';
import type { SearchFacet } from '#/modules/search/types/SearchFacet';
import type { SearchType } from '#/modules/search/types/SearchType';
import { useModal } from '#/zustand-stores/modalStore';

import { useAssignSearchPageUrlParametersToZustandStore } from './assignSearchPageUrlParametersToZustandStore';

type Args = {
  searchType: SearchType;
  searchFacets: SearchFacet[];
  getSearchTrackingData: () => Record<string, unknown>;
  isSearchLandingPage?: boolean;
};

export function useSearchPageSyncUrlAndFetch({
  searchType,
  searchFacets,
  getSearchTrackingData,
  isSearchLandingPage,
}: Args) {
  const { jobFamilyId } = useSearchJobsRouteLoaderData();

  const [searchParams] = useSearchParams();

  const searchLocation = useSearchLocation();
  const { fetchSearchPageResults } = useSearchActions();

  const { mainSearchReady } = useSearchReady();

  const [initialized, setInitialized] = useState(false);

  const initializedRef = useLatest(initialized);

  /*
   * Assign params to zustand & fetch results
   */

  // Don't trigger fetch just because the modal opened/closed
  const { modal } = useModal();
  const modalShownRef = useLatest(Boolean(modal));

  // Don't trigger fetch just because tracking data changed
  const getSearchTrackingDataRef = useLatest(getSearchTrackingData);

  const { assignSearchPageUrlParametersToZustandStore } =
    useAssignSearchPageUrlParametersToZustandStore();

  const fetch = useCallback(async () => {
    if (!isSearchLandingPage) {
      assignSearchPageUrlParametersToZustandStore({
        searchType,
        searchParams,
        searchFacets,
        familyId: jobFamilyId,
      });
    }

    const results = await fetchSearchPageResults();

    if (!initializedRef.current) {
      setInitialized(true);
    }

    if (modalShownRef.current) return;

    searchTrackDisplayedListings(
      results.hits,
      getSearchTrackingDataRef.current(),
    );
  }, [
    assignSearchPageUrlParametersToZustandStore,
    searchType,
    searchParams,
    searchFacets,
    jobFamilyId,
    fetchSearchPageResults,
    initializedRef,
    isSearchLandingPage,
    modalShownRef,
    getSearchTrackingDataRef,
  ]);

  /*
   * Re-fetch once url or search location change
   */
  useEffect(() => {
    if (!mainSearchReady) return;
    void fetch();
  }, [
    fetch,
    mainSearchReady,
    // Trigger fetch on search location change
    searchLocation,
  ]);

  return { getSearchTrackingData, initialized };
}
